import type { z } from 'zod';
import { zodToJsonSchema } from 'zod-to-json-schema';
import type { HttpClient } from '@wix/cli-http-client';
import { extensionFieldDefinitionSchema } from './types.js';
import { extensionHandlers } from '../extensionHandlers.js';
import type { GenerateExtensionType } from '../extensionHandlers.js';

function processExtensionFields(
  jsonSchema: Record<string, unknown> | undefined,
  zodDef: { description?: string }
): Record<string, unknown> | undefined {
  if (!jsonSchema) {
    return jsonSchema;
  }

  let result = { ...jsonSchema };

  // We use the .describe when defining fields and it will contains ExtensionFieldDefinition,
  // The description field is optional so we remove it if it's undefined but keep everything else
  if (zodDef.description) {
    try {
      const parsed = extensionFieldDefinitionSchema.parse(
        JSON.parse(zodDef.description)
      );
      result = { ...result, ...parsed };

      if (!parsed.description) {
        delete result.description;
      }
    } catch {
      // not a defineField value, keep as-is
    }
  }

  return result;
}

function getExtensionInputSchema(
  zodSchema: z.ZodTypeAny
): Record<string, unknown> {
  return zodToJsonSchema(zodSchema, {
    postProcess: processExtensionFields,
  });
}

export async function buildGenerateExtensionsSchema(
  httpClient: HttpClient,
  type?: GenerateExtensionType
) {
  const entries = Object.entries(extensionHandlers).filter(
    ([extensionType]) => !type || extensionType === type
  );

  const definitions: Record<string, Record<string, unknown>> = {};

  for (const [extensionType, def] of entries) {
    const zodSchema =
      typeof def.zodSchema === 'function'
        ? await def.zodSchema(httpClient)
        : def.zodSchema;

    const { $schema: _$schema, ...inputSchema } =
      getExtensionInputSchema(zodSchema);

    definitions[extensionType] = {
      title: def.title,
      description: def.description,
      group: def.group,
      documentation: def.documentation,
      ...inputSchema,
    };
  }

  return {
    $schema: 'http://json-schema.org/draft-07/schema#',
    schemaVersion: 1,
    definitions,
    oneOf: entries.map(([extensionType]) => ({
      $ref: `#/definitions/${extensionType}`,
    })),
  };
}
